export const normalize = value => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const unique = values => [...new Set(values.filter(Boolean))];
const words = text => ` ${normalize(text).replace(/[^a-z0-9]+/g, ' ')} `;
const includes = (text, term) => words(text).includes(` ${normalize(term)} `);
const COLOR_TERMS = {
  negro:['negro','negra','negros','negras','black'],
  marfil:['blanco','blanca','blancos','blancas','marfil','crudo','cruda','ivory','white','crema'],
  beige:['beige','arena','taupe','topo'],
  camel:['camel','cuero','canela','caramelo'],
  chocolate:['chocolate','marron','marrones','brown','cafe'],
  'azul marino':['azul marino','marino','marina','navy'],
  azul:['azul','azules','blue','celeste'],
  verde:['verde','verdes','oliva','salvia','petroleo','esmeralda','green'],
  rojo:['rojo','roja','rojos','rojas','red'],
  burdeos:['burdeos','granate','borgona'],
  dorado:['dorado','dorada','dorados','doradas','oro','gold'],
  plata:['plata','plateado','plateada','plateados','silver'],
  rosa:['rosa','rosado','rosada','fucsia'],
  violeta:['violeta','morado','morada','lila','purpura'],
  gris:['gris','grises','grey','gray'],
  naranja:['naranja','teja','terracota'],
  amarillo:['amarillo','amarilla','mostaza']
};
const COLOR_PAIRS = {
  negro:['negro','marfil','dorado','plata','burdeos'],
  marfil:['camel','chocolate','negro','dorado'],
  camel:['marfil','beige','verde','dorado','chocolate'],
  chocolate:['marfil','beige','verde','dorado','camel'],
  'azul marino':['marfil','camel','burdeos','plata'],
  verde:['marfil','camel','chocolate','dorado'],
  rojo:['negro','marfil','chocolate','dorado'],
  burdeos:['negro','marfil','chocolate','dorado']
};
const OCCASION_TERMS = {diario:['diario','diaria','cotidiano','cotidiana','casual','dia a dia'],trabajo:['trabajo','oficina','profesional'],fiesta:['fiesta','noche','celebracion'],invitada:['invitada','boda','ceremonia','evento','comunion','bautizo']};
const STYLE_TERMS = {basico:['basico','basica','minimalista','esencial'],romantico:['romantico','romantica'],bohemio:['bohemio','bohemia','boho'],clasico:['clasico','clasica','atemporal'],atrevido:['atrevido','atrevida','audaz','rockero','rockera']};
const detect = (text, dictionary) => Object.entries(dictionary).filter(([,terms])=>terms.some(t=>includes(text,t))).map(([key])=>key);
export function colorsIn(text) {
  let found = detect(text,COLOR_TERMS);
  if(found.includes('azul marino')) found=found.filter(c=>c!=='azul');
  return found;
}
function explicitOrDetected(values, dictionary, evidence) {
  return Array.isArray(values) && values.length ? unique(values.map(normalize)) : detect(evidence,dictionary);
}
function familyOf(product) {
  if(['ropa','joyeria','complementos','bolsos'].includes(product.family)) return product.family;
  const type=normalize(product.type);
  if(/bolso|clutch|bandolera/.test(type)) return 'bolsos';
  if(/anillo|pendiente|collar|colgante|pulsera|brazalete/.test(type)) return 'joyeria';
  if(/cinturon|fajin|cartera|casquete|tocado|panuel|bufanda/.test(type)) return 'complementos';
  if(/camis|vestido|cazadora|conjunto|jean|blusa|chaqueta|falda|pantalon|vaquero|chaleco|top|abrigo|lencero|cardigan|jersey|capa|americana|ameriacana/.test(type)) return 'ropa';
  return '';
}
function typeOf(product) {
  const type=normalize(product.type||product.title.split(' ')[0]);
  if(/pendiente/.test(type)) return 'pendientes';
  if(/anillo/.test(type)) return 'anillo';
  if(/collar|colgante/.test(type)) return 'collar';
  if(/pulsera|brazalete/.test(type)) return 'pulsera';
  return type;
}
export function profile(product, variantId) {
  const variant=product.variants?.find(v=>String(v.id)===String(variantId)) ?? product.variants?.find(v=>String(v.id)===String(product.selected)) ?? product.variants?.[0];
  const evidence=[product.title,...(product.tags??[]),product.description].join(' ');
  const colorValue=product.colorPosition>0 ? variant?.options?.[product.colorPosition-1] : '';
  let colors=colorsIn(colorValue);
  if(!colors.length && product.colors?.length) colors=unique(product.colors.flatMap(colorsIn));
  if(!colors.length) colors=colorsIn(product.title);
  if(!colors.length) colors=colorsIn((product.tags??[]).join(' '));
  if(!colors.length) {
    const match=normalize(product.description).match(/(?:color|tono)\s+([^.,;:]{1,40})/);
    if(match) colors=colorsIn(match[1]);
  }
  const markedFinish=normalize(product.finish);
  let finish=markedFinish;
  if(!finish) {
    const gold=/\b(?:dorado|dorada|dorados|doradas|oro)\b/.test(normalize(evidence));
    const silver=/\b(?:plateado|plateada|plateados|plata)\b/.test(normalize(evidence));
    finish=gold&&silver?'mixto':gold?'dorado':silver?'plateado':'';
  }
  const patternEvidence=[product.title,colorValue,String(product.description??'').slice(0,200)].join(' ');
  const patterned=/\b(?:estampad[oa]s?|leopardo|cebra|zebra|lunares|floral|flores|animal print)\b/.test(normalize(patternEvidence));
  return {family:familyOf(product),type:typeOf(product),colors,finish,patterned,occasions:explicitOrDetected(product.occasions,OCCASION_TERMS,evidence),styles:explicitOrDetected(product.styles,STYLE_TERMS,evidence),variant};
}
const overlaps=(a,b)=>a.some(x=>b.includes(x));
function clothingGroup(type) {
  if(/pantalon|vaquero|jean|falda/.test(type)) return 'bottom';
  if(/camis|blusa|top|lencero/.test(type)) return 'top';
  if(/chaqueta|cazadora|chaleco|abrigo|cardigan|jersey|capa|americana|ameriacana/.test(type)) return 'layer';
  if(/vestido|conjunto/.test(type)) return 'whole';
  return '';
}
function allowedPair(source,candidate) {
  if(!source.family||!candidate.family) return false;
  if(source.family!==candidate.family) return true;
  if(source.family==='joyeria') return source.type!==candidate.type;
  if(source.family==='ropa') {
    const a=clothingGroup(source.type),b=clothingGroup(candidate.type);
    return a!==b && ((a==='top'&&b==='bottom')||(a==='bottom'&&b==='top')||(a==='whole'&&b==='layer')||(a==='layer'&&b==='whole'));
  }
  return false;
}
function colorScore(a,b) {
  if(!a.length||!b.length) return 0;
  if(overlaps(a,b)) return 4;
  if(a.some(x=>b.some(y=>COLOR_PAIRS[x]?.includes(y)||COLOR_PAIRS[y]?.includes(x)))) return 5;
  if(a.some(c=>['negro','marfil','beige','gris'].includes(c))||b.some(c=>['negro','marfil','beige','gris'].includes(c))) return 2;
  return -1;
}
function rank(source,candidate) {
  if(!allowedPair(source,candidate)) return -Infinity;
  if(source.patterned&&candidate.patterned) return -Infinity;
  if(source.occasions.length&&candidate.occasions.length&&!overlaps(source.occasions,candidate.occasions)) return -Infinity;
  const color=colorScore(source.colors,candidate.colors);
  if(color<0) return -Infinity;
  const metals=['dorado','plateado'];
  if(metals.includes(source.finish)&&metals.includes(candidate.finish)&&source.finish!==candidate.finish) return -Infinity;
  let score=color;
  if(overlaps(source.occasions,candidate.occasions)) score+=6;
  if(overlaps(source.styles,candidate.styles)) score+=3;
  if(source.finish&&source.finish===candidate.finish&&source.finish!=='sin metal') score+=2;
  return score>=2?score:-Infinity;
}
export function recommend(source,catalog,{variantId,max=4,excludedIds=[]}={}) {
  const origin=profile(source,variantId);
  const excluded=new Set([String(source.id),...excludedIds.map(String)]);
  const manual=(source.manual??[]).map(String);
  const candidates=[];
  for(const product of catalog) {
    if(excluded.has(String(product.id))||!product.image) continue;
    let best=null;
    for(const variant of product.variants??[]) {
      if(!variant.available||!(Number(variant.price)>0)) continue;
      const p=profile(product,variant.id);
      const score=rank(origin,p);
      const manualIndex=manual.indexOf(String(product.id));
      if(manualIndex>=0 || Number.isFinite(score)) {
        const next={product,variant,profile:p,score,manualIndex};
        if(!best||score>best.score) best=next;
      }
    }
    if(best) candidates.push(best);
  }
  candidates.sort((a,b)=>{
    if(a.manualIndex>=0||b.manualIndex>=0) return (a.manualIndex<0?999:a.manualIndex)-(b.manualIndex<0?999:b.manualIndex);
    return b.score-a.score||String(a.product.id).localeCompare(String(b.product.id));
  });
  const selected=[],families=new Map(),types=new Set();
  for(const item of candidates) {
    if(selected.length>=Math.min(max,4)) break;
    const family=item.profile.family;
    const type=`${family}:${item.profile.type}`;
    const manualChoice=item.manualIndex>=0;
    if(!manualChoice && (types.has(type)||(families.get(family)??0)>=1)) continue;
    selected.push(item);types.add(type);families.set(family,(families.get(family)??0)+1);
  }
  return selected;
}
let catalogPromise;
async function fetchCatalog(baseUrl) {
  const products=[];
  for(let page=1;page<=125;page++) {
    const url=new URL(baseUrl,window.location.origin);
    url.searchParams.set('section_id','kartana-look-catalog');
    url.searchParams.set('page',String(page));
    const preview=new URL(window.location.href).searchParams.get('preview_theme_id');
    if(preview) url.searchParams.set('preview_theme_id',preview);
    const response=await fetch(url,{credentials:'same-origin',signal:AbortSignal.timeout(20000)});
    if(!response.ok) throw new Error('Look catalog unavailable');
    const html=new DOMParser().parseFromString(await response.text(),'text/html');
    const json=html.querySelector('[data-kartana-look-catalog]');
    if(!json) throw new Error('Look catalog missing');
    const data=JSON.parse(json.textContent);
    if(Number(data.page)!==page||!Array.isArray(data.products)) throw new Error('Invalid look catalog pagination');
    products.push(...data.products);
    if(page>=Number(data.pages)) return [...new Map(products.map(p=>[String(p.id),p])).values()];
  }
  throw new Error('Look catalog pagination limit');
}
if(typeof document!=='undefined' && !customElements.get('kartana-complete-look')) {
  customElements.define('kartana-complete-look',class extends HTMLElement {
    connectedCallback() {
      if(this.controller) return;
      this.controller=new AbortController();
      this.source=JSON.parse(this.querySelector('[data-look-source]').textContent);
      this.variantId=new URL(window.location.href).searchParams.get('variant')||this.source.selected;
      const signal=this.controller.signal;
      document.addEventListener('variant:update',event=>{
        if(String(event.detail?.data?.productId)!==String(this.source.id)) return;
        this.variantId=event.detail?.resource?.id||this.source.selected;
        if(this.catalog) this.render();
      },{signal});
      document.addEventListener('cart:update',()=>{if(this.catalog) this.render();},{signal});
      window.addEventListener('popstate',()=>{this.variantId=new URL(window.location.href).searchParams.get('variant')||this.source.selected;if(this.catalog)this.render();},{signal});
      this.observer=new IntersectionObserver(entries=>{
        if(entries.some(entry=>entry.isIntersecting)) {this.observer.disconnect();this.load();}
      },{rootMargin:'600px'});
      this.observer.observe(this);
    }
    disconnectedCallback() {this.controller?.abort();this.observer?.disconnect();this.controller=null;}
    async load() {
      try {
        catalogPromise??=fetchCatalog(this.dataset.catalogUrl).catch(error=>{catalogPromise=null;throw error;});
        this.catalog=await catalogPromise;
        if(this.isConnected) await this.render();
      } catch {this.dataset.state='unavailable';}
    }
    async render() {
      const revision=(this.revision??0)+1;this.revision=revision;
      let excludedIds=[];
      try {
        const response=await fetch(`${window.Shopify?.routes?.root||'/'}cart.js`,{credentials:'same-origin',signal:AbortSignal.timeout(8000)});
        if(response.ok) excludedIds=(await response.json()).items.map(item=>item.product_id);
      } catch {}
      if(revision!==this.revision||!this.isConnected) return;
      const recommendations=recommend(this.source,this.catalog,{variantId:this.variantId,max:Number(this.dataset.maxProducts)||4,excludedIds});
      const grid=this.querySelector('[data-look-grid]');grid.replaceChildren();
      const currency=new Intl.NumberFormat(this.dataset.locale||'es',{style:'currency',currency:this.dataset.currency||'EUR'});
      for(const {product,variant} of recommendations) {
        const item=document.createElement('li');item.className='kartana-complete-look__card';
        const link=document.createElement('a');
        const url=new URL(product.url,window.location.origin);url.searchParams.set('variant',variant.id);
        if(url.origin!==window.location.origin) continue;
        link.href=url.pathname+url.search;
        const image=document.createElement('img');image.src=variant.image||product.image;image.alt=product.title;image.width=600;image.height=750;image.loading='lazy';image.decoding='async';
        const title=document.createElement('h3');title.className='kartana-complete-look__title';title.textContent=product.title;
        const price=document.createElement('p');price.className='kartana-complete-look__price';price.textContent=currency.format(variant.price/100);
        const label=document.createElement('span');label.className='kartana-complete-look__link';label.textContent=this.dataset.linkLabel;
        link.append(image,title,price,label);item.append(link);grid.append(item);
      }
      this.querySelector('[data-look-content]').hidden=grid.children.length===0;
      this.dataset.state='ready';
    }
  });
}

